import React, { useEffect, useMemo, useState } from 'react'
import {
  Box, Typography, IconButton, Tooltip, Divider,
  List, ListItemButton, Chip, CircularProgress,
} from '@mui/material'
import CfpSelect from '../../comps/CfpSelect'
import AddIcon from '@mui/icons-material/Add'
import RefreshIcon from '@mui/icons-material/Refresh'
import SaveIcon from '@mui/icons-material/Save'
import EditIcon from '@mui/icons-material/Edit'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined'
import { addToast } from '../../services/Toasts'
import { FS_MD, FS_SM, FS_XS, ICON_MD, ICON_SM, COLOR_PRIMARY, COLOR_PRIMARY_HOVER1, COLOR_PRIMARY_HOVER2 } from '../../common/theme'
import ResizableDivider from '../../comps/ResizableDivider'
import { saveStyleGroup, renameStyleGroup, deleteStyleGroup, deleteStyleTag } from '../../services/TemplatesService'
import InputModal from '../modals/InputModal'
import ConfirmModal from '../modals/ConfirmModal'

// Tweak tag types:
//   `els` - element / storyboard tweak (extra storyboard-time instructions)
//   `ela` - element / answer tweak (extra answer-time instructions)
// Groups (the parent objects) are stored on the backend under group-type
// `els`, which is what /api2/smrename and /api2/smdelete expect.
type TweakTagType = 'els' | 'ela'
const GROUP_TYPE = 'els'

interface TweakTag {
  tagsha?: string
  type: TweakTagType
  tagnamehtml?: string
  valuehtml?: string
  // Server-side, every tag carries its group's name. Required on save.
  name?: string
}

interface TweakGroup {
  name: string
  styletags?: TweakTag[]
  updated?: string
}

interface ManageTweaksTabProps {
  groups: TweakGroup[]
  onRefresh?: () => void
}

const editableSx = {
  fontSize: FS_MD, lineHeight: 1.5, px: 0.5, py: 0.25,
  border: '1px solid transparent', borderRadius: 0.5,
  '&:focus': { outline: 'none', borderColor: 'primary.main', borderStyle: 'solid' },
  '&:empty::before': { content: 'attr(data-placeholder)', color: 'text.disabled', pointerEvents: 'none' },
}

export default function ManageTweaksTab({ groups, onRefresh }: ManageTweaksTabProps) {
  const [selectedName, setSelectedName] = useState<string | null>(null)
  const [sortProp, setSortProp] = useState('name')
  const [addOpen, setAddOpen] = useState(false)
  const [renameOpen, setRenameOpen] = useState(false)
  const [deleteGroupOpen, setDeleteGroupOpen] = useState(false)
  const [deleteItemTarget, setDeleteItemTarget] = useState<TweakTag | null>(null)
  const [saving, setSaving] = useState(false)
  const [lhsWidth, setLhsWidth] = useState(220)

  // Local working copy - edits go here, Save POSTs the whole group via
  // /api2/smsavestylegroup. Re-syncs whenever the parent refreshes.
  const [draft, setDraft] = useState<TweakGroup | null>(null)

  // The backend ships a "Standard" group as a read-only seed - hide it.
  const editableGroups = useMemo(() => groups.filter(g => g.name !== 'Standard'), [groups])

  // Auto-select the first editable group once it lands.
  useEffect(() => {
    if (selectedName == null && editableGroups.length > 0) {
      setSelectedName(editableGroups[0].name)
    }
  }, [editableGroups, selectedName])

  // Pull the canonical group from props, mirror into draft on selection or
  // upstream refresh.
  useEffect(() => {
    const found = editableGroups.find(g => g.name === selectedName) ?? null
    setDraft(found ? structuredClone(found) as TweakGroup : null)
  }, [selectedName, editableGroups])

  const sorted = useMemo(() => (
    [...editableGroups].sort((a, b) => String((a as any)[sortProp] ?? '').localeCompare(String((b as any)[sortProp] ?? '')))
  ), [editableGroups, sortProp])

  const tagsByType = (type: TweakTagType): TweakTag[] =>
    (draft?.styletags ?? []).filter(t => t.type === type)

  const handleAddGroup = async (name: string) => {
    if (!name.trim()) return
    try {
      // Seed with one placeholder tag so the backend accepts the group
      // (matches angularjs `addstyletemplatefn`).
      await saveStyleGroup({
        name: name.trim(),
        styletags: [{ name: name.trim(), type: GROUP_TYPE, value: '' }],
      })
      addToast('success', 'Tweak group added.')
      setSelectedName(name.trim())
      onRefresh?.()
    } catch {
      addToast('error', 'Failed to add tweak group.')
    }
  }

  const handleRenameGroup = async (newname: string) => {
    if (!draft || !newname.trim()) return
    try {
      await renameStyleGroup(draft.name, newname.trim(), GROUP_TYPE)
      addToast('success', 'Tweak group renamed.')
      setSelectedName(newname.trim())
      onRefresh?.()
    } catch {
      addToast('error', 'Failed to rename tweak group.')
    }
  }

  const handleDeleteGroup = async () => {
    if (!draft) return
    try {
      await deleteStyleGroup(draft.name, GROUP_TYPE)
      addToast('success', 'Tweak group deleted.')
      setSelectedName(null)
      onRefresh?.()
    } catch {
      addToast('error', 'Failed to delete tweak group.')
    }
  }

  const handleSaveGroup = async () => {
    if (!draft) return
    setSaving(true)
    try {
      // Every tag must carry the group's name on save (backend uses tag.name
      // to locate the parent group - same payload shape as smsave for styles).
      const payload = {
        ...draft,
        styletags: (draft.styletags ?? []).map(t => ({ ...t, name: draft.name })),
      }
      await saveStyleGroup(payload)
      addToast('success', 'Tweak group saved.')
      onRefresh?.()
    } catch {
      addToast('error', 'Failed to save tweak group.')
    } finally {
      setSaving(false)
    }
  }

  const handleAddTag = (type: TweakTagType) => {
    setDraft(prev => prev
      ? { ...prev, styletags: [...(prev.styletags ?? []), { name: prev.name, type, tagnamehtml: '', valuehtml: '' }] }
      : prev
    )
  }

  const handleEditTagName = (tag: TweakTag, html: string) => {
    setDraft(prev => prev
      ? { ...prev, styletags: (prev.styletags ?? []).map(t => t === tag ? { ...t, tagnamehtml: html } : t) }
      : prev
    )
  }

  const handleEditTagValue = (tag: TweakTag, html: string) => {
    setDraft(prev => prev
      ? { ...prev, styletags: (prev.styletags ?? []).map(t => t === tag ? { ...t, valuehtml: html } : t) }
      : prev
    )
  }

  const handleDeleteItem = async () => {
    if (!deleteItemTarget) return
    try {
      if (deleteItemTarget.tagsha) {
        await deleteStyleTag(deleteItemTarget.tagsha)
      }
      setDraft(prev => prev
        ? { ...prev, styletags: (prev.styletags ?? []).filter(t => t !== deleteItemTarget) }
        : prev
      )
      addToast('success', 'Tweak deleted.')
      if (deleteItemTarget.tagsha) onRefresh?.()
    } catch {
      addToast('error', 'Failed to delete tweak.')
    } finally {
      setDeleteItemTarget(null)
    }
  }

  const renderSection = (title: string, type: TweakTagType, valuePlaceholder: string) => {
    const tags = tagsByType(type)
    return (
      <Box sx={{ mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', px: 0.5, py: 0.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, flex: 1 }}>{title}</Typography>
          <Tooltip title="Add Tweak">
            <IconButton size="small" onClick={() => handleAddTag(type)}>
              <AddIcon sx={{ fontSize: ICON_MD }} />
            </IconButton>
          </Tooltip>
        </Box>
        <Divider sx={{ mb: 0.5 }} />
        {tags.length === 0 && (
          <Typography variant="caption" sx={{ opacity: 0.5, px: 0.5, display: 'block', py: 0.25 }}>None</Typography>
        )}
        {tags.map((tag, i) => (
          <Box key={tag.tagsha ?? `new-${type}-${i}`} sx={{ display: 'flex', alignItems: 'flex-start', py: 0.5, gap: 0.5 }}>
            <Box
              component="span"
              contentEditable
              suppressContentEditableWarning
              data-placeholder="Name"
              onBlur={(e: React.FocusEvent<HTMLSpanElement>) => handleEditTagName(tag, e.currentTarget.innerHTML)}
              dangerouslySetInnerHTML={{ __html: tag.tagnamehtml ?? '' }}
              sx={{ ...editableSx, width: 140, flexShrink: 0 }}
            />
            <Box
              component="span"
              contentEditable
              suppressContentEditableWarning
              data-placeholder={valuePlaceholder}
              onBlur={(e: React.FocusEvent<HTMLSpanElement>) => handleEditTagValue(tag, e.currentTarget.innerHTML)}
              dangerouslySetInnerHTML={{ __html: tag.valuehtml ?? '' }}
              sx={{ ...editableSx, flex: 1 }}
            />
            <Tooltip title="Delete Tweak">
              <IconButton
                size="small"
                onClick={() => setDeleteItemTarget(tag)}
                sx={{ opacity: 0.45, '&:hover': { opacity: 1 }, flexShrink: 0 }}
              >
                <DeleteOutlineIcon sx={{ fontSize: ICON_SM }} />
              </IconButton>
            </Tooltip>
          </Box>
        ))}
      </Box>
    )
  }

  return (
    <Box data-testid="bme_root" sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0, flex: 1 }}>

      {/* Modals */}
      <InputModal
        open={addOpen}
        title="Add Tweak Group"
        okLabel="Add"
        placeholder="Group name"
        singleLine
        testIdPrefix="bme_addgroup"
        onConfirm={handleAddGroup}
        onClose={() => setAddOpen(false)}
      />
      <InputModal
        open={renameOpen}
        title="Rename Tweak Group"
        okLabel="Rename"
        placeholder="New name"
        singleLine
        initialValue={draft?.name ?? ''}
        testIdPrefix="bme_renamegroup"
        onConfirm={handleRenameGroup}
        onClose={() => setRenameOpen(false)}
      />
      <ConfirmModal
        open={deleteGroupOpen}
        title="Delete Tweak Group"
        body={`Are you sure you want to delete "${draft?.name}"?`}
        okLabel="Delete"
        testIdPrefix="bme_deletegroup"
        onConfirm={handleDeleteGroup}
        onClose={() => setDeleteGroupOpen(false)}
      />
      <ConfirmModal
        open={!!deleteItemTarget}
        title="Delete Tweak"
        body="Are you sure you want to delete this tweak?"
        okLabel="Delete"
        testIdPrefix="bme_deletetag"
        onConfirm={handleDeleteItem}
        onClose={() => setDeleteItemTarget(null)}
      />

      {/* Toolbar */}
      <Box sx={{ display: 'flex', alignItems: 'center', px: 1, py: 0.75, gap: 0.5, flexShrink: 0 }}>
        <Typography variant="caption" sx={{ fontWeight: 600, flex: 1 }}>Tweak Groups</Typography>
        <Tooltip title="Refresh">
          <IconButton data-testid="bme_refresh_btn" size="small" onClick={onRefresh}>
            <RefreshIcon sx={{ fontSize: ICON_MD }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Add Group">
          <IconButton data-testid="bme_add_group_btn" size="small" onClick={() => setAddOpen(true)}>
            <AddIcon sx={{ fontSize: ICON_MD }} />
          </IconButton>
        </Tooltip>
      </Box>
      <Divider />

      {/* Split panel */}
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden', minHeight: 0 }}>

        {/* LHS list */}
        <Box sx={{ width: lhsWidth, flexShrink: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', px: 1, py: 0.5, borderBottom: 1, borderColor: 'divider', flexShrink: 0, gap: 0.5 }}>
            <CfpSelect
              label="Order by"
              value={sortProp}
              onChange={setSortProp}
              options={[
                { value: 'name', label: 'Name' },
                { value: 'updated', label: 'Updated' },
              ]}
            />
          </Box>
          <List disablePadding sx={{ flex: 1, overflowY: 'auto' }}>
            {sorted.length === 0 && (
              <Box sx={{ px: 2, py: 2, textAlign: 'center' }}>
                <Typography variant="caption" sx={{ opacity: 0.4, fontSize: FS_XS }}>
                  No tweak groups yet
                </Typography>
              </Box>
            )}
            {sorted.map((s, i) => {
              const isSelected = s.name === selectedName
              return (
                <ListItemButton
                  key={s.name ?? i}
                  data-testid="bme_group_row"
                  data-group-name={s.name}
                  selected={isSelected}
                  onClick={() => setSelectedName(s.name)}
                  sx={{
                    py: 1.5, px: 1.5,
                    borderLeft: 3, borderLeftColor: 'transparent',
                    transition: 'background-color 0.12s, border-color 0.12s',
                    '&:not(.Mui-selected)': {
                      bgcolor: i % 2 === 1 ? 'rgba(0,0,0,0.08)' : 'transparent',
                      '&:hover': { bgcolor: COLOR_PRIMARY_HOVER1 },
                    },
                    '&.Mui-selected': {
                      bgcolor: COLOR_PRIMARY_HOVER2,
                      borderLeftColor: COLOR_PRIMARY,
                      '&:hover': { bgcolor: COLOR_PRIMARY_HOVER2 },
                    },
                  }}
                >
                  <Typography variant="caption" sx={{
                    fontSize: FS_SM,
                    fontWeight: isSelected ? 600 : 400,
                    color: isSelected ? 'primary.main' : 'text.primary',
                  }}>{s.name}</Typography>
                </ListItemButton>
              )
            })}
          </List>
        </Box>

        {/* Panel divider - drag to resize the LHS list */}
        <ResizableDivider onDelta={(dx) => setLhsWidth(w => Math.max(140, Math.min(500, w + dx)))} />

        {/* RHS */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', px: 1, py: 0.75, gap: 0.5, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
            {draft ? (
              <Chip size="small" icon={<LayersOutlinedIcon />} label={draft.name} sx={{ mr: 'auto', fontSize: FS_XS }} />
            ) : (
              <Box sx={{ flex: 1 }} />
            )}
            <Tooltip title="Save Group">
              <span>
                <IconButton data-testid="bme_save_group_btn" size="small" disabled={!draft || saving} onClick={handleSaveGroup}>
                  {saving ? <CircularProgress size={14} /> : <SaveIcon sx={{ fontSize: ICON_MD }} />}
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Rename Group">
              <span>
                <IconButton data-testid="bme_rename_group_btn" size="small" disabled={!draft} onClick={() => setRenameOpen(true)}>
                  <EditIcon sx={{ fontSize: ICON_MD }} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Delete Group">
              <span>
                <IconButton data-testid="bme_delete_group_btn" size="small" disabled={!draft} onClick={() => setDeleteGroupOpen(true)}>
                  <DeleteOutlineIcon sx={{ fontSize: ICON_MD }} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>

          {draft ? (
            <Box sx={{ flex: 1, overflowY: 'auto', p: 1 }}>
              {renderSection('Custom Storyboard Tweaks', 'els', 'Extra instructions for the AI when constructing Storyboards')}
              {renderSection('Custom Answer Tweaks', 'ela', 'Extra instructions for the AI when constructing Answers')}
            </Box>
          ) : (
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography variant="caption" sx={{ opacity: 0.4 }}>Select a group</Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  )
}

